import { MantineColor } from '@mantine/core';
import { Badge } from 'src/design/atoms/Badge';
import { Card } from 'src/design/atoms/Card';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Title } from 'src/design/atoms/Title';
import { IconCircleCheck, IconDotsCircleHorizontal } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobs } from 'src/hooks/useJobs';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { TopicIssueDto } from 'src/api/FiretrolApiClient';
import { useMeetingIssues } from 'src/api/queries/meetings/useMeetingIssues';
import { formatDate, formatDateTime } from 'src/utils';

interface IssuesTableProps {
  jobId: number;
}

const PRIORITY_COLORS: Record<string, MantineColor> = {
  Low: 'blue',
  Medium: 'yellow',
  High: 'red',
} as const;

const getColumns = (): MRT_ColumnDef<TopicIssueDto>[] => {
  return [
    {
      accessorKey: 'meetingType',
      header: 'Meeting Type',
      accessorFn: (row) => {
        const topics = row.topics;
        if (!topics || topics.length === 0) return 'N/A';
        return topics[0].topic?.meeting?.meetingType?.name ?? 'N/A';
      },
      size: 150,
    },
    {
      accessorKey: 'meeting',
      header: 'Meeting',
      accessorFn: (row) => {
        const topics = row.topics;
        if (!topics || topics.length === 0) return 'Untitled';
        const title = topics[0].topic?.meeting?.title;
        return title ? title : 'Untitled';
      },
      size: 200,
    },
    {
      accessorKey: 'title',
      header: 'Issue',
      Cell: ({ cell }) => {
        const row = cell.row.original;
        return (
          <Group gap="xs">
            {row.complete ? (
              <IconCircleCheck
                color="var(--mantine-color-primary-6)"
                size={18}
              />
            ) : (
              <IconDotsCircleHorizontal
                color="var(--mantine-color-gray-6)"
                size={18}
              />
            )}
            {row.title}
          </Group>
        );
      },
      size: 300,
    },
    {
      accessorKey: 'topicTitle',
      header: 'Topic',
      accessorFn: (row) => {
        const topics = row.topics;
        if (!topics || topics.length === 0) return 'Untitled';
        const title = topics[0].topic?.title;
        return title ? title : 'Untitled';
      },
      size: 200,
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      Cell: ({ cell }) => {
        const priority = cell.getValue<string>();
        if (!priority) return null;
        return <Badge color={PRIORITY_COLORS[priority]}>{priority}</Badge>;
      },
      size: 100,
    },
    {
      accessorKey: 'assignee.fullName',
      header: 'Assigned To',
      accessorFn: (row) => row.assignee?.fullName ?? 'Unassigned',
      Cell: ({ cell }) => {
        const assignee = cell.getValue<string>();
        return <Badge variant="light">{assignee}</Badge>;
      },
      size: 150,
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      accessorFn: (row) => row.dueDate,
      Cell: ({ cell }) => {
        const dueDate = cell.getValue<Date | null>();
        if (!dueDate) {
          return <Badge variant="light">No Due Date</Badge>;
        }

        const isPastDue =
          moment(dueDate).isBefore(moment()) && !cell.row.original.complete;
        return (
          <Badge color={isPastDue ? 'red' : undefined} variant="light">
            {formatDateTime(dueDate, 'MM/DD/YY h:mma')}
          </Badge>
        );
      },
      size: 120,
    },
  ];
};

const renderStat = (label: string, stat: number, loading: boolean) => {
  return (
    <Card>
      <Title order={3} ta="center" mt={0}>
        {label}
      </Title>
      <Title order={2} ta="center" mb={0} c="dimmed">
        {loading ? <Loader size="sm" /> : stat}
      </Title>
    </Card>
  );
};

export const IssuesTable: React.FC<IssuesTableProps> = ({ jobId }) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const { data, isLoading: loading } = useMeetingIssues(jobId);
  const issues = data?.issues ?? [];
  const stats = {
    myItems: data?.myItems ?? 0,
    pastDue: data?.pastDue ?? 0,
    priority: data?.priority ?? 0,
    open: data?.open ?? 0,
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'issues-table',
    columns: columns,
    defaultSorting: [{ id: 'dueDate', desc: false }],
  });

  const tableExports = useTableExports<TopicIssueDto>(
    'meeting-issues',
    (data) => {
      return data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Meeting Type']:
          item.topics?.[0]?.topic?.meeting?.meetingType?.name || 'N/A',
        ['Meeting']: item.topics?.[0]?.topic?.meeting?.title || 'Untitled',
        ['Issue']: item.title,
        ['Topic']: item.topics?.[0]?.topic?.title || '',
        ['Priority']: item.priority || '',
        ['Assigned To']: item.assignee?.fullName || '',
        ['Due Date']: item.dueDate ? formatDate(item.dueDate) : '',
        ['Complete']: item.complete ? 'Yes' : 'No',
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const issue = row.original;
        if (!issue.topics || issue.topics.length === 0) return null;
        const topic = issue.topics[0];
        return (
          <>
            <EditActionIcon
              onClick={() => {
                navigate(
                  `/jobs/${jobId}/meetings/edit/${topic.topic.meeting.id}`,
                );
              }}
            />
          </>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: issues,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: issues.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <>
      <SimpleGrid cols={4} mb="lg">
        {renderStat('Past Due', stats.pastDue, loading)}
        {renderStat('High Priority', stats.priority, loading)}
        {renderStat('All Open', stats.open, loading)}
        {renderStat('My Items', stats.myItems, loading)}
      </SimpleGrid>

      {table}
    </>
  );
};
